import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuthUser } from "../../contexts/AuthUserContext";
import { useShop } from "../../contexts/ShopContext";
import { usePageHeader } from "../../contexts/PageHeaderContext";
import {
  fetchBenchmarkReport,
  fetchGscReport,
  fetchGmcReport,
  fetchRevenueRealityReport,
  fetchMarketReport,
  fetchMarketReportV2,
  fetchTargetAudienceReport,
  fetchCompetitorReport,
  fetchYearlyContentReport,
  fetchIdeationReport,
  fetchSeoReport,
  fetchAdsAuditReport,
  fetchInfluencerReport,
  fetchDailyOverviewReport,
  fetchHomepageAssessmentReport,
  fetchConversionAndPdpReport,
  fetchSocialMediaRawReport,
} from "../../utils/api/reportApi";
import pageStyles from "../../styles/PageLayout.module.css";
import reportDetailStyles from "../ReportDetailPage/ReportDetailPage.module.css";
import styles from "./GenericReportOpenPage.module.css";

const FETCHER_BY_KEY = {
  benchmark:             fetchBenchmarkReport,
  shop_seo_reports:      fetchSeoReport,
  google_search_console: fetchGscReport,
  google_merchant_center:fetchGmcReport,
  revenue_reality:       fetchRevenueRealityReport,
  market:                fetchMarketReport,
  market_v2:             fetchMarketReportV2,
  target_audience:       fetchTargetAudienceReport,
  competitor:            fetchCompetitorReport,
  yearlyContent:         fetchYearlyContentReport,
  ideation:              fetchIdeationReport,
  adsAudit:              fetchAdsAuditReport,
  influencer:            fetchInfluencerReport,
  dailyOverview:         fetchDailyOverviewReport,
  homepageAssessment:    fetchHomepageAssessmentReport,
  conversion:            fetchConversionAndPdpReport,
  socialMedia:           fetchSocialMediaRawReport,
};

const REPORT_OPEN_TITLE_BY_KEY = {
  benchmark:             "Store Summary",
  shop_seo_reports:      "SEO / AEO Audit",
  google_search_console: "Google Search Console",
  google_merchant_center:"Google Merchant Center",
  revenue_reality:       "Revenue Reality Report",
  market:                "Market Analysis",
  market_v2:             "Market Analysis V2",
  target_audience:       "Target Audience",
  competitor:            "Competitor Analysis",
  yearlyContent:         "Monthly Events Calendar",
  ideation:              "Monthly Ideation Report",
  adsAudit:              "Ads Audit Report",
  influencer:            "Influencer & Trending Report",
  dailyOverview:         "Daily Overview",
  homepageAssessment:    "Home Page Assessment",
  conversion:            "Conversion & PDP Analysis",
  socialMedia:           "Social Media Report",
};

const RETRY_DELAYS_MS = [0, 700, 1400, 2200];

const GenericReportOpenPage = () => {
  const { key } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthUser();
  const { selectedShop, shopData } = useShop();
  const { setPageHeader, clearPageHeader } = usePageHeader();

  const authToken = user?.token;
  const userId = user?._id || user?.id || user?.userId;
  const shop = selectedShop || shopData || null;
  const trace = location.state?.reportOpenTrace || null;
  const [traceId] = useState(
    () => trace?.traceId || `report-open:${key}:${Date.now()}:route`,
  );
  const [status, setStatus] = useState("loading");

  // Seed the header immediately with the report title so it shows during the skeleton.
  // ReportDetailPage will overwrite this with real metadata once the report loads.
  useEffect(() => {
    const title = REPORT_OPEN_TITLE_BY_KEY[key] || "Opening report…";
    setPageHeader({ title, updatedAt: null, source: "report-open", entityId: key });
    return () => clearPageHeader();
  }, [key, setPageHeader, clearPageHeader]);

  const isMongoId = (value) => /^[a-f0-9]{24}$/i.test(String(value || ""));
  const shopId = isMongoId(shop?._id)
    ? shop._id
    : isMongoId(shop?.id)
      ? shop.id
      : null;

  const attemptsRef = useRef(0);

  useEffect(() => {
    let active = true;
    if (!authToken || !userId || !shopId) return undefined;

    const fetcher = FETCHER_BY_KEY[key];
    if (!fetcher) {
      setStatus("error");
      return undefined;
    }

    attemptsRef.current = 0;

    const attempt = async () => {
      try {
        console.log("[report-open][frontend] generic-route.attempt", {
          key,
          traceId,
          attempt: attemptsRef.current + 1,
          userId,
          shopId,
        });
        const response = await fetcher({ userId, shopId, authToken, traceId });
        if (!active) return;

        const report = response?.report || null;
        const reportId = report?._id || report?.id || null;
        console.log("[report-open][frontend] generic-route.fetch_complete", {
          key,
          traceId,
          attempt: attemptsRef.current + 1,
          reportId,
          generationKey: report?.data?.generation_key || null,
          collectionName: report?.data?.collection_name || null,
        });

        if (reportId) {
          console.log("[report-open][frontend] generic-route.navigate", {
            key,
            traceId,
            reportId,
          });
          navigate(`/reports/${reportId}`, {
            replace: true,
            state: {
              prefetchedReport: report,
              reportOpenTrace: {
                traceId,
                source: "generic-route",
                clickStartedAtMs: trace?.clickStartedAtMs || Date.now(),
              },
            },
          });
          return;
        }

        attemptsRef.current += 1;
        if (attemptsRef.current < RETRY_DELAYS_MS.length) {
          const delay = RETRY_DELAYS_MS[attemptsRef.current];
          setStatus("loading");
          setTimeout(() => { if (active) attempt(); }, delay);
        } else {
          setStatus("pending");
          console.log("[report-open][frontend] generic-route.retries_exhausted", {
            key,
            traceId,
          });
        }
      } catch (error) {
        console.log("[report-open][frontend] generic-route.fetch_error", {
          key,
          traceId,
          attempt: attemptsRef.current + 1,
          error: error?.message || String(error),
        });
        if (!active) return;
        attemptsRef.current += 1;
        if (attemptsRef.current < RETRY_DELAYS_MS.length) {
          const delay = RETRY_DELAYS_MS[attemptsRef.current];
          setTimeout(() => { if (active) attempt(); }, delay);
        } else {
          setStatus("error");
        }
      }
    };

    attempt();

    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken, shopId, userId, key]);

  return (
    <section
      className={`${pageStyles.page} ${reportDetailStyles.wrapper}`}
      data-page="report-detail"
    >
      <div className={reportDetailStyles.splitShell}>
        <div className={reportDetailStyles.viewerPane}>
          <div className={reportDetailStyles.viewerCard}>
            <div className={reportDetailStyles.viewerHeader}>
              <section className={styles.hero}>
                <div className={styles.titleBlock}>
                  <div className={`${styles.bar} ${styles.titleBar}`} />
                </div>
                <div className={styles.tabRow}>
                  <div className={`${styles.pill} ${styles.pillActive}`} />
                  <div className={styles.pill} />
                </div>
              </section>
            </div>

            <div className={reportDetailStyles.viewerScroll}>
              <section className={styles.panel}>
                <div className={styles.contentBlock}>
                  <div className={`${styles.bar} ${styles.blockTitle}`} />
                  <div className={styles.lineGroup}>
                    {[92, 78, 85, 65, 88].map((w) => (
                      <div
                        key={w}
                        className={`${styles.bar} ${styles.line}`}
                        style={{ width: `${w}%` }}
                      />
                    ))}
                  </div>
                </div>

                <div className={styles.metricGrid}>
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className={styles.metricCard}>
                      <div className={`${styles.bar} ${styles.metricLabel}`} />
                      <div className={`${styles.bar} ${styles.metricValue}`} />
                    </div>
                  ))}
                </div>

                <div className={styles.contentBlock}>
                  <div className={`${styles.bar} ${styles.blockTitle}`} />
                  <div className={styles.lineGroup}>
                    {[88, 72, 95, 60].map((w) => (
                      <div
                        key={w}
                        className={`${styles.bar} ${styles.line}`}
                        style={{ width: `${w}%` }}
                      />
                    ))}
                  </div>
                </div>

                {status === "pending" && (
                  <p className={styles.statusText}>
                    Preparing your report&hellip;
                  </p>
                )}
                {status === "error" && (
                  <p className={styles.statusText}>
                    Could not load report. Please try again.
                  </p>
                )}
              </section>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GenericReportOpenPage;
